import {
  ArrowUp,
  ArrowUpRight,
  Command,
  Copy,
  Download,
  EyeOff,
  Laptop,
  Menu,
  PanelTop,
  Route,
  Server,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Waves,
  X,
  createIcons,
} from "lucide";
import "./styles.css";

const iconSet = {
  ArrowUp,
  ArrowUpRight,
  Command,
  Copy,
  Download,
  EyeOff,
  Laptop,
  Menu,
  PanelTop,
  Route,
  Server,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Waves,
  X,
};

createIcons({ icons: iconSet });

const menuButton = document.querySelector("[data-menu-button]");
const mobileMenu = document.querySelector("[data-mobile-menu]");

menuButton?.addEventListener("click", () => {
  const willOpen = mobileMenu.hasAttribute("hidden");
  mobileMenu.toggleAttribute("hidden", !willOpen);
  menuButton.setAttribute("aria-expanded", String(willOpen));
  menuButton.setAttribute("aria-label", willOpen ? "Close menu" : "Open menu");
  menuButton.innerHTML = `<i data-lucide="${willOpen ? "x" : "menu"}" aria-hidden="true"></i>`;
  createIcons({ icons: iconSet });
});

mobileMenu?.querySelectorAll("a").forEach((link) => {
  link.addEventListener("click", () => {
    mobileMenu.setAttribute("hidden", "");
    menuButton.setAttribute("aria-expanded", "false");
    menuButton.setAttribute("aria-label", "Open menu");
    menuButton.innerHTML = '<i data-lucide="menu" aria-hidden="true"></i>';
    createIcons({ icons: iconSet });
  });
});

const header = document.querySelector("[data-header]");
const updateHeader = () => header?.classList.toggle("is-scrolled", window.scrollY > 8);
updateHeader();
window.addEventListener("scroll", updateHeader, { passive: true });

const observer = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      }
    });
  },
  { threshold: 0.12 },
);

document.querySelectorAll(".reveal").forEach((element, index) => {
  element.style.transitionDelay = `${Math.min(index % 4, 3) * 70}ms`;
  observer.observe(element);
});

const toast = document.querySelector("[data-toast]");
let toastTimer;
const showToast = (message) => {
  if (!toast) return;
  toast.textContent = message;
  toast.classList.add("is-visible");
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove("is-visible"), 1800);
};

const setupCommands = `git clone https://gitea.lhqs.ink/project-list/lhqs-mac-panel.git
cd lhqs-mac-panel
make install
open /Applications/lhqsMacPanel.app`;

document.querySelector("[data-copy-setup]")?.addEventListener("click", async () => {
  await navigator.clipboard.writeText(setupCommands);
  showToast("Setup commands copied");
});

const examples = [
  {
    prompt: "Turn these notes into three clear action items.",
    lines: [
      "Consolidate the launch notes into a one-page brief.",
      "Assign owners and dates before Friday's review.",
      "Share the final plan with the product team.",
    ],
  },
  {
    prompt: "Make this email shorter and more direct.",
    lines: [
      "Lead with the decision and remove the background paragraph.",
      "Replace passive language with a clear owner and deadline.",
      "Close with one specific request for confirmation.",
    ],
  },
];

const panel = document.querySelector("[data-demo-panel]");
const prompt = document.querySelector("[data-demo-prompt]");
const response = document.querySelector("[data-demo-response]");
const status = document.querySelector("[data-demo-status]");
let exampleIndex = 0;
let isDemoRunning = false;

const responseMarkup = (lines) => lines
  .map((line, index) => `<p><span class="number">0${index + 1}</span>${line}</p>`)
  .join("");

const runDemo = () => {
  if (!prompt || !response || !status || isDemoRunning) return;
  isDemoRunning = true;
  exampleIndex = (exampleIndex + 1) % examples.length;
  const example = examples[exampleIndex];
  prompt.textContent = example.prompt;
  response.innerHTML = "";
  response.classList.add("is-loading");
  status.textContent = "Receiving";

  example.lines.forEach((line, index) => {
    setTimeout(() => {
      response.insertAdjacentHTML("beforeend", responseMarkup([line]).replace("01", `0${index + 1}`));
      if (index === example.lines.length - 1) {
        response.classList.remove("is-loading");
        status.textContent = "Complete";
        isDemoRunning = false;
      }
    }, 420 + index * 480);
  });
};

document.querySelector("[data-send-demo]")?.addEventListener("click", runDemo);
document.querySelector("[data-close-demo]")?.addEventListener("click", () => {
  panel?.classList.add("is-dismissed");
  showToast("Press Option + Space to bring it back");
  setTimeout(() => panel?.classList.remove("is-dismissed"), 1100);
});
document.querySelector("[data-copy-response]")?.addEventListener("click", async () => {
  await navigator.clipboard.writeText(response?.innerText ?? "");
  showToast("Response copied");
});

document.querySelector("[data-year]").textContent = new Date().getFullYear();
